import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";

const SIZE = {
  large:  { value: "text-[22px] font-bold",   skel: "h-7 w-24" },
  medium: { value: "text-[18px] font-bold",   skel: "h-5 w-20" },
  small:  { value: "text-[14px] font-semibold", skel: "h-4 w-16" },
};

function formatINR(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const v = Math.abs(Number(n));
  const sign = n < 0 ? "-" : "";
  if (v >= 1_00_00_000) return `${sign}₹${(v / 1_00_00_000).toFixed(2)}Cr`;
  if (v >= 1_00_000)    return `${sign}₹${(v / 1_00_000).toFixed(1)}L`;
  if (v >= 1000)        return `${sign}₹${(v / 1000).toFixed(1)}K`;
  return `${sign}₹${Math.round(v).toLocaleString("en-IN")}`;
}

function formatValue(value, format) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (Number.isNaN(n) && format !== "raw") return null;
  switch (format) {
    case "currency":   return formatINR(n);
    case "percentage": return `${n.toFixed(1)}%`;
    case "days":       return `${n.toFixed(1)} days`;
    case "number":     return n.toLocaleString("en-IN");
    default:           return String(value);
  }
}

/**
 * MetricDisplay — drop-in replacement for a metric number on a KPI card.
 *
 * Renders one of three states:
 *   1. Real value          : formatted normally
 *   2. Dash + tooltip      : when value is null/0 and data is missing
 *   3. Skeleton             : when isLoading is true
 *
 * The hover tooltip explains why the metric matters or what to connect.
 */
export default function MetricDisplay({
  value,
  format = "currency",
  label,
  tooltipText,
  size = "large",
  isLoading = false,
  testId,
}) {
  const sz = SIZE[size] || SIZE.large;
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null); // viewport-fixed {top, left, arrowLeft}
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const TOOLTIP_W = 220;
    const screenW = window.innerWidth;
    let left = rect.left;
    if (left + TOOLTIP_W + 8 > screenW) left = screenW - TOOLTIP_W - 8;
    left = Math.max(8, left);
    const arrowLeft = Math.min(TOOLTIP_W - 16, Math.max(12, rect.left + rect.width / 2 - left));
    setCoords({ top: rect.bottom + 8, left, arrowLeft });
  }, [open]);

  if (isLoading) {
    return (
      <div data-testid={testId || "metric-skel"} className={`${sz.skel} animate-pulse rounded-md bg-slate-200`} />
    );
  }

  const v = Number(value);
  // v1.4.5 — only null / undefined / NaN / empty-string are "missing".
  // Genuine 0 (e.g. `gst_payable = 0` when a quarter has no tax due)
  // is a real value and must render as "₹0", not as a dash with
  // "Connect Tally" tooltip. The previous `v === 0` branch destroyed
  // the distinction between "no sync yet" and "synced, answer is 0"
  // — surfacing the wrong empty-state CTA on real data
  // (Turing Eastland CFO Overview, 2026-06-24).
  const isMissing =
    value === null
    || value === undefined
    || (typeof value === "number" && !Number.isFinite(value))
    || (typeof value === "string" && value.trim() === "");

  if (isMissing) {
    // Real-data mode: show dash + info icon. Demo mode: just a grey dash.
    return (
      <div
        ref={wrapRef}
        data-testid={testId || `metric-missing-${(label || "").replace(/\s+/g, "-").toLowerCase()}`}
        className="relative inline-flex items-center gap-1 text-slate-400"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        tabIndex={0}
      >
        <span className={sz.value} style={{ color: "#9CA3AF" }}>—</span>
        <Info
          size={14}
          className="text-slate-400"
          data-testid={`metric-info-${(label || "").replace(/\s+/g, "-").toLowerCase()}`}
        />
        {open && tooltipText && coords && createPortal(
          <div
            role="tooltip"
            data-testid={`metric-tooltip-${(label || "").replace(/\s+/g, "-").toLowerCase()}`}
            className="fixed z-[1000] w-[220px] rounded-md bg-[#1E3A5F] px-3 py-2 text-[12px] leading-snug text-white shadow-lg"
            style={{ top: coords.top, left: coords.left }}
          >
            {tooltipText}
            <span
              className="absolute -top-1 h-2 w-2 rotate-45 bg-[#1E3A5F]"
              style={{ left: coords.arrowLeft }}
            />
          </div>,
          document.body
        )}
      </div>
    );
  }

  return (
    <span
      data-testid={testId || `metric-value-${(label || "").replace(/\s+/g, "-").toLowerCase()}`}
      className={`${sz.value} text-[#0F1B2D]`}
    >
      {formatValue(value, format)}
    </span>
  );
}
